import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";

interface IScrolledToBottomContextType {
  hasScrolledToBottom: boolean;
}

interface IScrolledToBottomProviderProps {
  children: ReactNode;
}

const SCROLL_BOTTOM_THRESHOLD = 200;

const ScrolledToBottomContext = createContext<IScrolledToBottomContextType>({
  hasScrolledToBottom: false,
});

export const ScrolledToBottomProvider = ({ children }: IScrolledToBottomProviderProps) => {
  const value = useScrolledToBottomProvider();
  return <ScrolledToBottomContext.Provider value={value}>{children}</ScrolledToBottomContext.Provider>;
};

export const useScrolledToBottomContext = (): IScrolledToBottomContextType => useContext(ScrolledToBottomContext);

const useScrolledToBottomProvider = (): IScrolledToBottomContextType => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const evaluateScrollPosition = useCallback((): void => {
    const documentHeight = document.body.scrollHeight;
    const currentScroll = window.scrollY + window.innerHeight;
    setHasScrolledToBottom(currentScroll + SCROLL_BOTTOM_THRESHOLD > documentHeight);
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        setHasScrolledToBottom(window.innerHeight >= document.body.clientHeight);
      });
    });

    resizeObserver.observe(document.body);

    const handleScroll = (): void => {
      requestAnimationFrame(evaluateScrollPosition);
    };

    document.addEventListener("scroll", handleScroll, { passive: true });
    evaluateScrollPosition();

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener("scroll", handleScroll);
    };
  }, [evaluateScrollPosition]);

  return {
    hasScrolledToBottom,
  };
};
